'use client'

import { AuthProvider } from '@/contexts/AuthContext'
import { EnterpriseSecurityEnforcer } from '@/components/auth/EnterpriseSecurityEnforcer'
import { EnterprisePasswordResetGate } from '@/components/auth/EnterprisePasswordResetGate'
import { SignedOutGate } from '@/components/auth/SignedOutGate'
import { FileDragProvider } from '@/contexts/FileDragContext'
import { useMemberships } from '@/hooks/useMemberships'
import { setGroup } from '@/lib/analytics'
import { useEffect, useRef, type ReactNode } from 'react'
import { initializeBandwidthMonitoring, stopBandwidthMonitoring } from '@/lib/BandwidthMonitor'
import { initializeNetworkAdaptation, stopNetworkAdaptation } from '@/lib/NetworkAdaptation'

/**
 * Associates the current user with their team groups in PostHog.
 * This enables browsing teams and seeing their members in PostHog's Groups section.
 */
function PostHogGroupIdentifier() {
  const { memberships } = useMemberships()
  const lastIdentifiedTeams = useRef<string>('')

  useEffect(() => {
    if (!memberships?.teamOrgs?.length) return

    // Dedupe: only re-identify if team list changed
    const teamKey = memberships.teamOrgs.map(t => t.id).join(',')
    if (teamKey === lastIdentifiedTeams.current) return
    lastIdentifiedTeams.current = teamKey

    for (const team of memberships.teamOrgs) {
      setGroup('team', team.id, {
        name: team.name,
        slug: team.slug,
      })
    }
  }, [memberships])

  return null
}

export function Providers({ children }: { children: ReactNode }) {
  // Initialize bandwidth monitoring and network adaptation on mount
  useEffect(() => {
    initializeBandwidthMonitoring()
    initializeNetworkAdaptation()

    return () => {
      stopNetworkAdaptation()
      stopBandwidthMonitoring()
    }
  }, [])

  return (
    <AuthProvider>
      <FileDragProvider>
        <EnterpriseSecurityEnforcer />
        <EnterprisePasswordResetGate />
        <PostHogGroupIdentifier />
        <SignedOutGate>{children}</SignedOutGate>
      </FileDragProvider>
    </AuthProvider>
  )
}
